/**
 * Copy text to the clipboard, tolerating insecure-context hosts.
 *
 * `navigator.clipboard` is only defined in a secure context (HTTPS or
 * `localhost`). On a plain-HTTP host such as `app.kit.local` it is
 * `undefined`, so `navigator.clipboard.writeText(...)` throws. Fall back to a
 * hidden-textarea `document.execCommand('copy')` so copying still works there.
 *
 * Returns `true` when the text reached the clipboard, `false` otherwise — the
 * caller decides how to surface success or failure.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Secure-context API rejected (e.g. permission denied) — try the legacy path.
  }
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    // Keep it off-screen so the page does not visibly jump while we select.
    textarea.style.position = 'fixed';
    textarea.style.top = '-1000px';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}
